import React, { useState, useEffect } from "react";
import { SmartTooltip, TableAdv, TagAdv, Button } from "../../../../../../components";
import { useIncentiveRoles } from "../IncentiveModuleLayout";
import { dateFormator, mapTableRows } from "../../../../../../utils";
import { fetchIncentiveSheet } from "../../../../../../redux/slices/incentives/incentivesApi";
import dayjs from "dayjs";
import { useSelector, useDispatch } from "react-redux";
import { getIncentiveSettingsStart } from "../../../../../../redux/slices/incentives/incentiveSlice";

const actionColorMap = {
  Paid: "success",
  Unpaid: "danger",
  Pending: "warning",
  Eligible: "indigo",
  Approved: "success",
};

const MasterSheet = () => {
    const dispatch = useDispatch();
    const { incentiveSettings } = useSelector((state) => state.incentive);
    const benchSettings = (incentiveSettings && incentiveSettings["INCENTIVE_BENCH"]) || {};
    const currency = benchSettings.currency || (benchSettings.value?.currency || "USD");
    const currencySymbol = currency === "INR" ? "₹" : "$";

  const { availableRoles, moduleName } = useIncentiveRoles();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedRows, setExpandedRows] = useState({});

  // Fetch settings if not already in Redux
  useEffect(() => {
    if (!incentiveSettings?.["INCENTIVE_BENCH"]) {
        dispatch(getIncentiveSettingsStart("INCENTIVE_BENCH"));
    }
  }, [dispatch, incentiveSettings]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // MasterSheet shows all records (Pending and Eligible/Approved pipeline)
        const res = await fetchIncentiveSheet({ module: "Bench" });
        // Map _id to id for TableAdv compatibility, handle object _id from new group stage
        const formattedRows = (res || []).map(row => {
            const stringId = typeof row._id === 'object' ? `${row._id.placementGrp}_${row._id.invoiceGrp}` : row._id;
            return {
                ...row,
                id: stringId
            };
        });
        
        // Group conceptually: Sort by Consultant Name, then Initial vs Recurrent
        formattedRows.sort((a, b) => {
           if (a.placementId === b.placementId) {
               const aRecurrent = a._id?.invoiceGrp && a._id.invoiceGrp !== "initial";
               const bRecurrent = b._id?.invoiceGrp && b._id.invoiceGrp !== "initial";
               return (aRecurrent === bRecurrent) ? 0 : aRecurrent ? 1 : -1; 
           }
           const nameA = a.consultantName || '';
           const nameB = b.consultantName || '';
           return nameA.localeCompare(nameB);
        });

        setRows(formattedRows);
      } catch (error) {
        console.error("Failed to load BenchSales master sheet data:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  const handleRowToggle = (rowId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const displayRows = React.useMemo(() => {
    const visible = [];
    const grouped = {};

    rows.forEach(r => {
      const pId = r.placementId;
      if (!grouped[pId]) grouped[pId] = { initial: null, children: [] };
      if (r._id?.invoiceGrp === "initial") grouped[pId].initial = r;
      else grouped[pId].children.push(r);
    });

    Object.keys(grouped).forEach(k => {
      const group = grouped[k];
      if (group.initial) {
        const parent = group.initial;
        parent.isParent = true;
        parent.hasChildren = group.children.length > 0;
        
        // Hide native UI status by injecting Blueprint purely for the physical display row
        visible.push({ ...parent, status: "Blueprint" });

        // If parent is physically expanded, inject children below it
        if (expandedRows[parent.id]) {
          group.children.forEach(child => visible.push({ ...child, isChildRow: true, parentId: k }));
        }
      } else {
        group.children.forEach(child => visible.push(child));
      }
    });

    return visible;
  }, [rows, expandedRows]);

  const columns = [
    {
      key: "consultantName",
      label: "Consultant Name",
      width: "min-w-[200px]! max-w-[250px]!",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.isChildRow && <span className="text-gray-300 ml-4">↳</span>}
          {(row.hasChildren || (row.teamMembers && row.teamMembers.length > 0)) && (
            <Button
              icon={
                expandedRows[row.id]
                  ? "keyboard_arrow_down"
                  : "keyboard_arrow_right"
              }
              type="icon"
              variant="text"
              size="xs"
              color="black"
              onClick={(e) => {
                e.stopPropagation();
                handleRowToggle(row.id);
              }}
            />
          )}
          <SmartTooltip
            description={row?.consultantName || "N/A"}
            size="xs"
            color="black"
            noicon
            tooltipPosition="bottom"
            portal={true}
          >
            <div className="line-clamp-1 font-medium">{row?.consultantName || "N/A"}</div>
          </SmartTooltip>
        </div>
      ),
    },
    {
      key: "jobTitle",
      label: "Consultant Job Title",
      width: "min-w-[150px]! max-w-[200px]!",
      render: (row) => (
        <SmartTooltip
          description={row?.jobTitle || "N/A"}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal={true}
        >
          <div className="line-clamp-1">{row?.jobTitle || "N/A"}</div>
        </SmartTooltip>
      ),
    },
    {
      key: "isRecurrent",
      label: "Type",
      width: "w-[120px]",
      render: (row) => {
        const isRecurrent = row._id?.invoiceGrp && row._id.invoiceGrp !== "initial";
        return (
          <TagAdv
             title={isRecurrent ? "Recurrent" : "Initial"}
             size="sm"
             color={isRecurrent ? "indigo" : "gray"}
             colorDepth={600}
             className="w-[80px]! text-center"
          />
        );
      },
    },
    {
      key: "clientName",
      label: "Client",
      width: "min-w-[150px]! max-w-[200px]!",
      render: (row) => (
        <SmartTooltip
          description={row?.clientName || "N/A"}
          size="xs"
          color="black"
          noicon
          tooltipPosition="bottom"
          portal={true}
        >
          <div className="line-clamp-1">{row?.clientName || "N/A"}</div>
        </SmartTooltip>
      ),
    },
    {
      key: "salary",
      label: "Total Salary",
      render: (row) => `$${row.salary || 0}`,
    },
    {
      key: "totalCommissionPercentage",
      label: "Total Incentive",
      render: (row) => {
        if (row.status === "Blueprint") return "--";
        const total = (row.teamMembers || []).reduce((sum, member) => sum + (Number(member.amount) || 0), 0);
        return `${currencySymbol}${total.toFixed(2)}`;
      },
    },
    {
      key: "totalCommissionAmount",
      label: "Commission",
      render: (row) => row.status === "Blueprint" ? "--" : `$${row.totalCommissionAmount || 0}`,
    },
    {
      key: "startDate",
      label: "Start Date",
      render: (row) => dateFormator(row.startDate),
    },
    {
      key: "unlockDate",
      label: "Unlock Date",
      render: (row) => row.unlockDate ? dateFormator(row.unlockDate) : "N/A",
    },
    {
      key: "status",
      label: "Status",
      width: "w-[120px]",
      render: (row) => {
        if (row.status === "Blueprint") {
            return (
              <div className="flex items-center">
                <TagAdv
                  title="--"
                  size="sm"
                  color="gray"
                  colorDepth={400}
                  className="w-[100px]! text-center"
                />
              </div>
            );
        }
        
        const statuses = [...new Set((row.teamMembers || []).map(m => m.status))];
        const displayStatus = statuses.length === 1 ? statuses[0] : (statuses.includes("Pending") ? "Pending" : "Mixed");
        return (
          <div className="flex items-center">
            <TagAdv
              title={displayStatus}
              size="sm"
              color={actionColorMap[displayStatus] || "gray"}
              colorDepth={600}
              className="w-[100px]! text-center"
            />
          </div>
        );
      },
    },
  ];

  const mappedRows = mapTableRows(displayRows, columns);

  const getResolvedRoleName = (item) => {
    // Bench module allows [1, 4]
    const allowedModules = [1, 4];
    const roleId = item.roleId || item.roleType;
    if (!roleId || !availableRoles?.length) return item.roleName || "Unknown";
    
    // First try to resolve by UIID or Type
    const role = availableRoles.find(r => String(r.uiid) === String(roleId) || String(r.type) === String(roleId));
    
    if (role && allowedModules.includes(role.module)) {
      return role.name;
    }
    return "Unknown";
  };

  const AccordionComponent = ({ row }) => {
    if (!row) return null;

    if (row.isParent) {
      const children = rows.filter(r => r.placementId === row.placementId && r._id?.invoiceGrp !== "initial");
      
      const totalGP = children.reduce((acc, curr) => acc + (Number(curr.totalBaseAmount) || 0), 0);
      
      let eligibleIncentives = 0;
      let pendingIncentives = 0;
      
      children.forEach(child => {
         if (child.teamMembers) {
             child.teamMembers.forEach(member => {
                 const amt = Number(member.amount) || 0;
                 if (member.status === "Eligible" || member.status === "Approved" || member.status === "Paid") {
                     eligibleIncentives += amt;
                 } else if (member.status === "Pending") {
                     pendingIncentives += amt;
                 }
             });
         }
      });

      const count = children.length;

      return (
        <div className="bg-[#f8fafc] border border-indigo-100 rounded-md p-5 m-2 flex flex-col gap-4 shadow-[0px_2px_10px_0px_#e0e7ff]">
          <div className="flex w-full items-start gap-12 flex-wrap">
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Total GP</span>
              <span className="text-xl font-semibold text-gray-800">${totalGP.toFixed(2)}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-bold text-indigo-400 uppercase tracking-wider">Eligible Incentives</span>
              <span className="text-xl font-semibold text-indigo-700">{currencySymbol}{eligibleIncentives.toFixed(2)}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-bold text-orange-400 uppercase tracking-wider">Pending Incentives</span>
              <span className="text-xl font-semibold text-orange-600">{currencySymbol}{pendingIncentives.toFixed(2)}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">Total Invoices</span>
              <span className="text-xl font-semibold text-gray-800">{count}</span>
            </div>
          </div>
          
          <div className="w-full h-px bg-indigo-100/60"></div>
          
          <div className="flex w-full items-start gap-8 flex-wrap">
            <div className="flex flex-col gap-1 min-w-[max-content]">
              <span className="text-xs font-semibold text-gray-500">
                Blueprint Config
              </span>
            </div>
            {row.teamMembers && row.teamMembers.map((item, idx) => {
                const resolvedRoleName = getResolvedRoleName(item);
                return (
                  <div key={idx} className="flex gap-2 items-center">
                    <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                      {resolvedRoleName}{" "}
                      <span className="text-gray-500 font-normal">({item.name})</span>
                    </span>
                    <span className="text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                      {currencySymbol}{item.rateUsed || 0}
                    </span>
                  </div>
                );
            })}
          </div>
        </div>
      );
    }

    if (!row.teamMembers || row.teamMembers.length === 0) return null;
    return (
      <div className="bg-[#FFF5EC] p-4 flex flex-col gap-2">
        <div className="flex w-full items-start gap-8 flex-wrap">
          <div className="flex flex-col gap-1 min-w-[max-content]">
            <span className="text-xs font-semibold text-gray-500">
              Invoice Number
            </span>
            {row.invoiceNum ? (
                <span 
                    className="text-sm font-medium text-gray-900"
                >
                  {row.invoiceNum}
                </span>
            ) : (
                <span className="text-sm font-medium text-gray-900">-</span>
            )}
          </div>
          <div className="flex flex-col gap-1 min-w-[max-content] border-l border-orange-200 pl-4">
            <span className="text-xs font-semibold text-gray-500 pt-1">
              {currency} Total
            </span>
          </div>

          {row.teamMembers.map((item, idx) => {
            const resolvedRoleName = getResolvedRoleName(item);
            return (
              <div key={idx} className="flex flex-col gap-1">
                <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                  {resolvedRoleName}{" "}
                  <span className="text-gray-500 font-normal">({item.name})</span>
                </span>
              <span className="text-sm font-medium text-gray-900 flex items-center gap-2">
                {item.amount !== null ? `${currencySymbol}${item.amount}` : ""}
              </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="h-full flex-1 w-full flex flex-col overflow-hidden">
      <TableAdv
        columns={columns}
        rows={mappedRows}
        toolbarRight={{ search: true }}
        accordion={<AccordionComponent />}
        expandedRows={expandedRows}
        onRowToggle={handleRowToggle}
        loading={loading}
        fixedCol={1}
      />
    </div>
  );
};

export default MasterSheet;
